import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";

import {
  useHookForm,
  useMerchantApiMutation,
  useMerchantApiQuery,
} from "../../../hooks";
import {
  IBank,
  IBusinessType,
  IOnboardingStatus,
  IOnboardingStep4Request,
  IOnboardingStepResponse,
  IRevenueBracket,
} from "../../../interfaces-and-types";
import { globalHandlerUtils } from "../../../utils";
import { IOnboardingStepProps, IOnboardingStepViewProps } from "../Onboarding";

import { View } from "./View";

export interface IOnboardingStep4ViewProps
  extends IOnboardingStepViewProps<IOnboardingStep4Request> {}

export const OnboardingStep4 = (props: IOnboardingStepProps) => {
  const {
    isLoading: isLoadingOnboardingStatus,
    isSuccess: isSuccessOnboardingStatus,
    data: onboardingStatus,
  } = useMerchantApiQuery<IOnboardingStatus>("getOnboardingStatus");

  const {
    isLoading: isLoadingStep4,
    isSuccess: isSuccessStep4,
    isError: isErrorStep4,
    data: apiResponse,
    mutate: saveOnboardingStep,
  } = useMerchantApiMutation<IOnboardingStep4Request, IOnboardingStepResponse>(
    "saveOnboardingStep4"
  ).usePostMutation;

  useEffect(() => {
    if (isSuccessStep4) {
      // display success message and request email confirmation
      props.handleNext();
    }
  }, [isSuccessStep4]);

  const initialFormValues: IOnboardingStep4Request = {
    two_factor_login_enabled_for_superadmin: false,
    two_factor_login_enabled_for_all_staff: false,
    two_factor_transfers_enabled: false,
    send_otp_to_email_only: false,
    use_authenticator_app: false,
  };

  const onboardingStep4Schema: yup.SchemaOf<IOnboardingStep4Request> = yup
    .object({
      two_factor_login_enabled_for_superadmin: yup
        .boolean()
        .required("Two-factor login setting for superadmin is required"),
      two_factor_login_enabled_for_all_staff: yup
        .boolean()
        .required("Two-factor login setting for staff is required"),
      two_factor_transfers_enabled: yup
        .boolean()
        .required("Setting is required"),
      send_otp_to_email_only: yup.boolean().required("Setting is required"),
      use_authenticator_app: yup.boolean().required("Setting is required"),
    })
    .required();

  const handleApiSubmit = (data: IOnboardingStep4Request) => {
    saveOnboardingStep(data);
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const { onFormSubmit, errors, register, reset } = useHookForm({
    initialFormValues,
    validationSchema: onboardingStep4Schema,
    onValid: handleApiSubmit,
    onInvalid: handleError,
  });

  useEffect(() => {
    if (isSuccessOnboardingStatus) {
      reset(onboardingStatus);
    }
  }, [isSuccessOnboardingStatus]);

  return (
    <View
      {...props}
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
    />
  );
};
